import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MoveRight } from "lucide-react";
import { MaskedLines } from "@/components/motion/MaskedLines";
import { Reveal } from "@/components/motion/Reveal";
import { MomentLinks, MomentProducts, MomentText } from "@/components/home/RitualParts";
import { RITUAL } from "@/data/content";

const Intro = ({ wide = false }) => (
  <div className={wide ? "flex w-[30vw] shrink-0 flex-col justify-center pr-8" : ""}>
    <p className="eyebrow">Il rituale quotidiano</p>
    <h2 className="mt-5 font-display text-5xl font-medium leading-[1.02] text-navy lg:text-6xl xl:text-7xl">
      <MaskedLines lines={["Un giorno", <em className="italic text-copper">con Perfero.</em>]} />
    </h2>
    <p className="mt-6 max-w-sm text-lg leading-relaxed text-slate2">Cinque momenti, dieci prodotti: la cura che accompagna ogni ora della giornata.</p>
    {wide && (
      <p className="mt-10 flex items-center gap-3 text-[14px] uppercase tracking-[0.25em] text-copper-deep">
        Continua a scorrere <MoveRight className="h-4 w-4" />
      </p>
    )}
  </div>
);

const DesktopCard = ({ moment }) => (
  <article data-testid={`ritual-moment-${moment.n}`} className="grid min-h-[64vh] w-[62vw] max-w-[1100px] shrink-0 grid-cols-2 overflow-hidden rounded-[2rem] border border-copper/25 bg-cream">
    <div className="flex flex-col justify-between gap-8 p-10 xl:p-12">
      <MomentText moment={moment} large />
      <MomentLinks slugs={moment.products} />
    </div>
    <div className="bg-parchment p-8">
      <MomentProducts slugs={moment.products} className="h-full" />
    </div>
  </article>
);

const RitualDesktop = () => {
  const ref = useRef(null);
  const trackRef = useRef(null);
  const [dist, setDist] = useState(0);

  useEffect(() => {
    const measure = () => {
      if (trackRef.current) setDist(Math.max(0, trackRef.current.scrollWidth - window.innerWidth));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, [0, 1], [0, -dist]);

  return (
    <div ref={ref} className="relative hidden lg:block" style={{ height: `calc(100vh + ${dist}px)` }}>
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden pt-24">
        <motion.div ref={trackRef} style={{ x }} className="flex w-max items-stretch gap-8 px-16">
          <Intro wide />
          {RITUAL.map((m) => (
            <DesktopCard key={m.n} moment={m} />
          ))}
        </motion.div>
        <div className="mx-16 mt-10 h-px bg-navy/15">
          <motion.div style={{ scaleX: scrollYProgress }} className="h-px origin-left bg-copper" />
        </div>
      </div>
    </div>
  );
};

const RitualMobile = () => (
  <div className="container-pf py-24 lg:hidden">
    <Intro />
    <div className="mt-12 space-y-8">
      {RITUAL.map((m) => (
        <Reveal key={m.n} className="overflow-hidden rounded-[2rem] border border-copper/25 bg-cream" data-testid={`ritual-mobile-${m.n}`}>
          <div className="h-72 bg-parchment p-5">
            <MomentProducts slugs={m.products} className="h-full" />
          </div>
          <div className="space-y-6 p-7">
            <MomentText moment={m} />
            <MomentLinks slugs={m.products} />
          </div>
        </Reveal>
      ))}
    </div>
  </div>
);

export const Ritual = () => (
  <section id="rituale" className="bg-sand/50" data-testid="ritual-section">
    <RitualDesktop />
    <RitualMobile />
  </section>
);
